import React, { useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import MediaAPI from "@/src/api/MediaAPI";

import { findInsertCuepointIndex } from "../../../utils/cuepoints";
import Button from "../../button/Button";
import Player, { convertMediaCuepointsToMarkers } from "../../player/Player";

import type { PlayerAPI, SmpteController } from "okast-player";
import type { JSX } from "react";
import type { UseFieldArrayReturn } from "react-hook-form";

import type { GetMediaResponse } from "../../../api/MediaAPI.types";
import type { CuepointsFormValues } from "../TabContentCuePoints";

interface CuePointsPlayerProps {
  item: GetMediaResponse;
  fieldArray: UseFieldArrayReturn<CuepointsFormValues, "cuepoints">;
}

function CuePointsPlayer({
  item,
  fieldArray,
}: CuePointsPlayerProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const [currentTimecode, setCurrentTimecode] = useState<string | null>(null);
  const smpteControllerRef = useRef<null | SmpteController>(null);

  const getMediaFastAssetsPreviewQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMediaFastAssetsPreview,
    params: [item.uuid],
  });

  const { data: mediaFastPreview } = useQuery(getMediaFastAssetsPreviewQuery);

  const { fields, insert } = fieldArray;

  const isTimecodeAlreadyExist = fields.some(
    (field) => field.timecode === currentTimecode,
  );

  const hasEmptyTimecode = fields.some((cuepoint) => cuepoint.timecode === "");

  const isDisabledExtractTimecodeButton =
    hasEmptyTimecode || isTimecodeAlreadyExist;

  const tooltipExtractTimecode = isTimecodeAlreadyExist
    ? t("existingTimecode")
    : t("existingEmptyTimecode");

  const onPlayerInit = (
    _: PlayerAPI,
    smpteController: SmpteController | null,
  ): void => {
    smpteControllerRef.current = smpteController;
  };

  const handleClickFrames = (frames: number): void => {
    smpteControllerRef?.current?.step(frames);
  };

  const handleClickExtractTimecode = (): void => {
    const currentTimecodeUpdated =
      smpteControllerRef?.current?.getCurrentSmpte();
    if (currentTimecodeUpdated) {
      const index = findInsertCuepointIndex(fields, currentTimecodeUpdated);

      insert(index, { timecode: currentTimecodeUpdated, enabled: true });
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTimecodeRefresh =
        smpteControllerRef?.current?.getCurrentSmpte();
      if (
        currentTimecodeRefresh &&
        currentTimecodeRefresh !== currentTimecode
      ) {
        setCurrentTimecode(currentTimecodeRefresh);
      }
    }, 250);

    return () => clearInterval(interval);
  }, [currentTimecode]);

  const playerParams = {
    source: mediaFastPreview?.url,
    type: "video/mp4",
    markers: convertMediaCuepointsToMarkers(item?.cuepoints || []),
    framerate: mediaFastPreview?.framerate,
  };

  return (
    <div className="flex size-full flex-col gap-6.25">
      <div className="relative flex aspect-video w-full bg-black">
        <div className="absolute z-10 flex w-full items-center justify-center p-3.75">
          <div className="text-overlay-foreground bg-black px-1.25 py-[0.15625rem] text-[1.171875rem]">
            {currentTimecode}
          </div>
        </div>
        {mediaFastPreview ? (
          <Player
            isFullSize
            autoPlay={false}
            onInit={onPlayerInit}
            playerParams={playerParams}
          />
        ) : null}
      </div>
      <div className="flex h-10 items-center justify-center gap-2.5">
        <Button
          onClick={(): void => handleClickFrames(-10)}
          size="md"
          type="button"
          variant="secondary"
        >
          {t("-10")}
        </Button>
        <Button
          onClick={(): void => handleClickFrames(-1)}
          size="md"
          type="button"
          variant="secondary"
        >
          {t("-1")}
        </Button>
        <Button
          data-tooltip-content={tooltipExtractTimecode}
          data-tooltip-hidden={!isDisabledExtractTimecodeButton}
          data-tooltip-id="extract-timecode-button"
          isDisabled={isDisabledExtractTimecodeButton}
          onClick={handleClickExtractTimecode}
          size="md"
          type="button"
        >
          {t("extractTimecode")}
        </Button>
        <Button
          onClick={(): void => handleClickFrames(1)}
          size="md"
          type="button"
          variant="secondary"
        >
          {t("+1")}
        </Button>
        <Button
          onClick={(): void => handleClickFrames(10)}
          size="md"
          type="button"
          variant="secondary"
        >
          {t("+10")}
        </Button>
      </div>
    </div>
  );
}

export default CuePointsPlayer;
